import { AlarmSettings } from "model/AlarmSettings";
import { AlarmsResponse, toAlarm } from "./Alarm";
import { keyBy, groupBy, toPairs } from 'lodash';
import { SystemModeValues, toSystemMode, fromSystemMode } from './Device';

export interface DeliverySettingsResponse {
  alarmId: number;
  systemMode: SystemModeValues;
  smsEnabled?: boolean;
  callEnabled?: boolean;
  emailEnabled?: boolean;
  pushEnabled?: boolean;
}

export interface AlarmSettingsItemResponse {
  deviceId?: string;
  locationId?: string;
  settings: DeliverySettingsResponse[];
}

export type AlarmSettingsResponse = Array<AlarmSettingsItemResponse>;

export interface GetAlarmSettingsResponse {
  items: AlarmSettingsResponse;
}

export interface AlarmSettingsPayload {
  items: AlarmSettingsResponse;
}

export interface GetAlarmSettingsPayload {
  locationIds?: string[];
  deviceIds?: string[];
}

export const toAlarmSettings = (alarmSetResp: AlarmSettingsResponse, alarmsResponse: AlarmsResponse): AlarmSettings[] => {
  const alarms = alarmsResponse.items
    .filter(a => !a.isInternal &&
                  a.active &&
                  a.deliveryMedium.userConfigurable &&
                  (a.deliveryMedium.call.supported ||
                  a.deliveryMedium.sms.supported ||
                  a.deliveryMedium.push.supported ||
                  a.deliveryMedium.email.supported)
                )
    .map(a => toAlarm(a));
  const alarmsMap = keyBy(alarms, 'id');

  return alarmSetResp.reduce((acc: AlarmSettings[], alarmSettings: AlarmSettingsItemResponse) => {
    const res: AlarmSettings[] = alarmSettings.settings
      .filter(s => alarmsMap[s.alarmId])
      .map(s => ({
        alarm: alarmsMap[s.alarmId],
        systemMode: toSystemMode(s.systemMode),
        callEnabled: s.callEnabled,
        emailEnabled: s.emailEnabled,
        pushEnabled: s.pushEnabled,
        smsEnabled: s.smsEnabled,
        deviceId: alarmSettings.deviceId,
        locationId: alarmSettings.locationId,
      }));
    return [ ...acc, ...res ]
  }, []);
}

const groupSettings = (data: AlarmSettings[], groupByFieldName: 'locationId' | 'deviceId'): AlarmSettingsResponse =>
  toPairs(groupBy(data, groupByFieldName))
  .filter(([deviceIdOrLocationId, alarmSettings]: [string, AlarmSettings[]]) => deviceIdOrLocationId !== 'undefined')
  .map(([deviceIdOrLocationId, alarmSettings]: [string, AlarmSettings[]]) => ({
    [groupByFieldName]: deviceIdOrLocationId,
    settings: alarmSettings.map(datum => ({
      alarmId: datum.alarm.id,
      systemMode: fromSystemMode(datum.systemMode),
      smsEnabled: datum.smsEnabled,
      callEnabled: datum.callEnabled,
      emailEnabled: datum.emailEnabled,
      pushEnabled: datum.pushEnabled,
    }))
  }));

export const fromAlarmSettings = (data: AlarmSettings[]): AlarmSettingsPayload => ({
  items: [
    ...groupSettings(data, 'locationId'),
    ...groupSettings(data, 'deviceId')
  ]
});
